/**
 * NotificationItem Component
 *
 * Notification card matching the Sonner toast chrome, scaled for the panel.
 */

import { useNavigate } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import { X } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNotificationContext } from '@/context/NotificationContext';
import { cn } from '@/lib/core/utils';
import type { Notification } from '@/types/notifications';
import { CATEGORY_CONFIG } from '@/types/notifications';

export type CenterToastTone = 'default' | 'success' | 'error' | 'warning';

const TONE_CLASS: Record<CenterToastTone, string> = {
  default: 'text-foreground',
  success: 'text-success',
  error: 'text-danger',
  warning: 'text-warning',
};

interface CenterToastProps {
  title: string;
  description?: string | null;
  icon?: React.ComponentType<{ className?: string }>;
  tone?: CenterToastTone;
  meta?: string;
  actionLabel?: string | null;
  onAction?: () => void;
  onDismiss: () => void;
  onActivate?: () => void;
  unread?: boolean;
}

export function CenterToast({
  title,
  description,
  icon: Icon,
  tone = 'default',
  meta,
  actionLabel,
  onAction,
  onDismiss,
  onActivate,
  unread,
}: CenterToastProps) {
  const toneClass = TONE_CLASS[tone];

  return (
    <div
      className={cn(
        'relative flex w-full items-start gap-2 rounded-md border border-input bg-background px-2 py-1.5 pr-7 text-left text-xs text-foreground',
        onActivate && 'cursor-pointer hover:bg-accent hover:text-accent-foreground',
      )}
      onClick={onActivate}
      role={onActivate ? 'button' : undefined}
      tabIndex={onActivate ? 0 : undefined}
      onKeyDown={onActivate ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onActivate();
        }
      } : undefined}
    >
      {Icon && <Icon className={cn('h-4 w-4 shrink-0', toneClass)} />}
      <div className="min-w-0 flex-1">
        <p className={cn('font-medium leading-5', toneClass)}>
          {title}
          {unread && (
            <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-primary align-middle" />
          )}
        </p>
        {description && (
          <p className="font-normal leading-snug text-muted-foreground line-clamp-2">
            {description}
          </p>
        )}
        {actionLabel && onAction && (
          <Button
            size="sm"
            variant="outline"
            className="mt-1.5 h-6 px-2 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onAction();
            }}
          >
            {actionLabel}
          </Button>
        )}
        {meta && (
          <p className="mt-1 text-2xs leading-none text-muted-foreground/70">{meta}</p>
        )}
      </div>
      <button
        type="button"
        className="absolute top-1.5 right-1.5 flex h-6 w-6 items-center justify-center rounded-md border-none bg-transparent p-0 text-muted-foreground hover:bg-accent hover:text-foreground"
        aria-label="Dismiss notification"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
      >
        <X className="h-3 w-3" strokeWidth={1.5} />
      </button>
    </div>
  );
}

interface NotificationItemProps {
  notification: Notification;
}

export function NotificationItem({ notification }: NotificationItemProps) {
  const navigate = useNavigate();
  const { markAsRead, dismiss, setIsOpen } = useNotificationContext();

  const categoryConfig = CATEGORY_CONFIG[notification.category];
  const CategoryIcon = (LucideIcons as Record<string, React.ComponentType<{ className?: string }>>)[categoryConfig.icon] || LucideIcons.Bell;
  const isUnread = !notification.read_at;
  const tone: CenterToastTone = notification.priority === 'urgent'
    ? 'error'
    : notification.priority === 'action_required'
      ? 'warning'
      : 'default';

  const open = async () => {
    if (isUnread) await markAsRead(notification.id);
    if (notification.action_url) {
      setIsOpen(false);
      navigate(notification.action_url);
    }
  };

  return (
    <CenterToast
      title={notification.title}
      description={notification.message}
      icon={CategoryIcon}
      tone={tone}
      unread={isUnread}
      meta={`${categoryConfig.label} · ${formatDistanceToNowStrict(new Date(notification.created_at), { addSuffix: true })}`}
      actionLabel={notification.action_url ? notification.action_label : undefined}
      onAction={notification.action_url && notification.action_label ? open : undefined}
      onActivate={open}
      onDismiss={() => { void dismiss(notification.id); }}
    />
  );
}
